/**
 * @fileoverview Category Extraction Panel
 *
 * Purpose: Displays category-specific extraction results for a document.
 * Shows a scrollable tab bar for each extraction category with status
 * indicators, extracted data, and error states for failed extractions.
 */

import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Badge } from "@/ui/primitives/badge";
import { Loader2, CheckCircle2, AlertTriangle, Clock } from "lucide-react";
import { EXTRACTION_CATEGORY_CONFIG } from "@cvx/lib/extractionCategories";
import { GenericExtractionRenderer } from "./GenericExtractionRenderer";

interface CategoryExtractionPanelProps {
  documentId: Id<"documents">;
}

type ExtractionRow = {
  _id: Id<"categoryExtractions">;
  extractionCategory: string;
  status: string;
  extractedData?: unknown;
  /** URL to fetch full extraction data when too large for inline storage */
  extractedDataUrl?: string;
  error?: string;
  startedAt?: number;
  completedAt?: number;
  schemaVersion?: string;
};

/**
 * Hook that fetches full extraction data from a storage URL when the inline
 * data is a lightweight summary (large transaction lists stored in file storage).
 */
function useFullExtractionData(
  url: string | undefined,
  extractionId: string | undefined,
) {
  const [fullData, setFullData] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!url) {
      setFullData(null);
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setFullData(data);
      }
    } catch (err) {
      console.error("[CategoryExtractions] Failed to fetch storage data:", err);
    } finally {
      setLoading(false);
    }
  }, [url]);

  useEffect(() => {
    setFullData(null);
    fetchData();
  }, [fetchData, extractionId]);

  return {
    data: fullData,
    isLoadingStorage: loading && !!url,
  };
}

function getStatusIcon(status: string) {
  switch (status) {
    case "completed":
      return <CheckCircle2 className="h-3 w-3 shrink-0 text-green-500" />;
    case "running":
      return (
        <Loader2 className="h-3 w-3 shrink-0 animate-spin text-blue-500" />
      );
    case "failed":
      return <AlertTriangle className="h-3 w-3 shrink-0 text-red-500" />;
    default:
      return <Clock className="h-3 w-3 shrink-0 text-muted-foreground" />;
  }
}

function getCategoryLabel(category: string): string {
  const config =
    EXTRACTION_CATEGORY_CONFIG[
      category as keyof typeof EXTRACTION_CATEGORY_CONFIG
    ];
  return config?.label ?? category;
}

export function CategoryExtractionPanel({
  documentId,
}: CategoryExtractionPanelProps) {
  const { data: rawExtractions } = useQuery(
    convexQuery(
      api.jobs.gatherInformation.extraction.queries
        .getDocumentCategoryExtractions,
      { documentId },
    ),
  );

  // Boundary: getDocumentCategoryExtractions declares `returns: v.array(v.any())`
  // in convex/jobs/gatherInformation/extraction/queries.ts, so its return type
  // does not infer. Type the rows here with the local ExtractionRow shape,
  // which mirrors the handler's actual output (a categoryExtractions doc plus
  // a resolved extractedDataUrl).
  const extractions = rawExtractions as ExtractionRow[] | undefined;

  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Compute selected tab and extraction (safe for hooks — no early returns before hooks)
  const firstCompleted =
    extractions?.find((e) => e.status === "completed")?.extractionCategory ??
    extractions?.[0]?.extractionCategory;

  const selectedTab =
    activeTab ?? firstCompleted ?? extractions?.[0]?.extractionCategory;
  const selectedExtraction = extractions?.find(
    (e) => e.extractionCategory === selectedTab,
  );

  // Hook MUST be called unconditionally (before any returns)
  const { data: storageData, isLoadingStorage } = useFullExtractionData(
    selectedExtraction?.extractedDataUrl,
    selectedExtraction?._id,
  );

  // Resolve: merge storage data (full transactions) into inline data (metadata/summary).
  // Storage data contains the full banking/ledger objects; inline has the lightweight
  // version with empty transaction arrays + metadata fields like summary, primaryEntity.
  const resolvedData = (() => {
    const inline = selectedExtraction?.extractedData as
      Record<string, unknown> | undefined;
    if (!storageData || !inline) return inline ?? storageData;
    const full = storageData as Record<string, unknown>;
    return { ...inline, ...full };
  })();

  if (!extractions || extractions.length === 0) return null;

  const completed = extractions.filter((e) => e.status === "completed").length;
  const total = extractions.length;
  const allComplete = completed === total;
  const anyFailed = extractions.some((e) => e.status === "failed");

  return (
    <div className="space-y-3">
      {/* Progress header */}
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium">Category Extractions</span>
        <Badge
          variant={
            allComplete ? "default" : anyFailed ? "destructive" : "secondary"
          }
          size="sm"
        >
          {allComplete ? `${total} complete` : `${completed}/${total} complete`}
        </Badge>
      </div>

      {/* Tab buttons — scrollable row of badges */}
      <div className="flex flex-wrap gap-1.5">
        {extractions.map((ext: ExtractionRow) => {
          const isActive = ext.extractionCategory === selectedTab;
          return (
            <button
              key={ext.extractionCategory}
              onClick={() => setActiveTab(ext.extractionCategory)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ${
                isActive
                  ? "border-foreground/20 bg-foreground/5 text-foreground shadow-sm"
                  : "border-transparent text-muted-foreground hover:bg-muted hover:text-foreground"
              }`}
            >
              {getStatusIcon(ext.status)}
              {getCategoryLabel(ext.extractionCategory)}
            </button>
          );
        })}
      </div>

      {/* Selected tab content */}
      {selectedExtraction && (
        <div className="pt-1">
          {selectedExtraction.status === "completed" && resolvedData ? (
            isLoadingStorage ? (
              <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Loading extraction data...</span>
              </div>
            ) : (
              <GenericExtractionRenderer
                category={selectedExtraction.extractionCategory}
                data={resolvedData}
              />
            )
          ) : selectedExtraction.status === "running" ? (
            <ExtractionProgress startedAt={selectedExtraction.startedAt} />
          ) : selectedExtraction.status === "failed" ? (
            <div className="flex flex-col items-center gap-2 py-8">
              <AlertTriangle className="h-5 w-5 text-red-500" />
              <span className="text-sm text-red-600">Extraction failed</span>
              {selectedExtraction.error && (
                <span className="max-w-md text-center text-xs text-muted-foreground">
                  {selectedExtraction.error}
                </span>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 py-8 text-muted-foreground">
              <Clock className="h-4 w-4" />
              <span className="text-sm">Pending...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Shows a progress indicator with elapsed time while extraction is running.
 */
function ExtractionProgress({ startedAt }: { startedAt?: number }) {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!startedAt) return;
    const update = () =>
      setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [startedAt]);

  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  const timeStr =
    minutes > 0
      ? `${minutes}m ${seconds.toString().padStart(2, "0")}s`
      : `${seconds}s`;

  // Progress stages based on typical extraction timing
  const stage =
    elapsed < 10
      ? "Preparing document..."
      : elapsed < 30
        ? "Parsing document structure..."
        : elapsed < 60
          ? "Extracting structured data..."
          : elapsed < 120
            ? "Processing large dataset..."
            : "Finalising extraction...";

  return (
    <div className="flex flex-col items-center gap-3 py-8">
      <Loader2 className="h-5 w-5 animate-spin text-blue-500" />
      <div className="flex flex-col items-center gap-1">
        <span className="text-sm text-foreground">{stage}</span>
        <span className="text-xs text-muted-foreground">{timeStr} elapsed</span>
      </div>
      {elapsed > 60 && (
        <span className="max-w-xs text-center text-xs text-muted-foreground">
          Large documents with many transactions can take a few minutes to
          process.
        </span>
      )}
    </div>
  );
}
